import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { FunctionComponent } from "react";

import { enUS as enUSPicker } from "@mui/x-date-pickers/locales";

import { enUS, fr, de } from "date-fns/locale";

import { format, isValid } from "date-fns";

import { Checkbox } from "@/components/Checkbox/Checkbox";

export type Schedule = {
	[key: string]: {
		active: boolean;
		fromTime: string;
		toTime: string;
	};
};

type ScheduleItemProps = {
	schedule: Schedule;
	setSchedule: (schedule: Schedule) => void;
	day: string;
	error?: boolean;
};

export const ScheduleItem: FunctionComponent<ScheduleItemProps> = ({
	schedule,
	day,
	setSchedule,
	error,
}) => {
	const userLocale = navigator.language || "en-US";

	const localeMap = {
		"en-US": enUS,
		"en-GB": enUS, // you can alias if you don’t mind slight differences
		"fr-FR": fr,
		"de-DE": de,
		de: de,
		en: enUS,
		fr: fr,
	};

	const currentLocale = localeMap?.[userLocale as keyof typeof localeMap] || enUS;

	return (
		<div className="flex flex-row gap-4 items-center">
			<div className="flex flex-col gap-2">
				<Checkbox
					error={error}
					isChecked={schedule?.[day]?.active}
					showError={!!error}
					onChange={() => {
						setSchedule({
							...schedule,
							[day]: {
								...schedule?.[day],
								active: !schedule?.[day]?.active,
							},
						});
					}}
				/>
			</div>
			<div className="text-text-meta text-sm font-medium w-[100px]">{day.capitalizeString()}</div>
			<div>
				<LocalizationProvider
					adapterLocale={currentLocale}
					dateAdapter={AdapterDateFns}
					localeText={enUSPicker.components.MuiLocalizationProvider.defaultProps.localeText}
				>
					<TimePicker
						sx={{
							width: "100%",
							height: 48,
							marginBottom: 1,
							"& .MuiSvgIcon-root": {
								fontSize: "18px",
							},
							"& .MuiPickersInputBase-root": {
								maxWidth: "150px",
								backgroundColor: "white",
								minHeight: 48,
								marginBottom: 0,
								fontSize: "14px",
							},
							"& .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersInputBase-root:hover": {
								borderColor: "#E5E7EB !important",
							},
							"& .MuiFilledInput-root": {
								backgroundColor: "white",
								minHeight: 48,
								marginBottom: 0,
							},
							"& .MuiFilledInput-input": {
								padding: 1,
							},
						}}
						value={
							schedule?.[day]?.fromTime
								? new Date(
										new Date().setHours(
											Number(schedule?.[day]?.fromTime.split(":")[0]),
											Number(schedule?.[day]?.fromTime.split(":")[1])
										)
									)
								: null
						}
						onChange={(value: Date | null) => {
							if (value && !isValid(value)) {
								return;
							}

							const formattedTime = value ? format(value, "HH:mm") : null;

							setSchedule({
								...schedule,
								[day]: { ...schedule?.[day], fromTime: formattedTime as string },
							});
						}}
					/>
				</LocalizationProvider>
			</div>

			<div>
				<LocalizationProvider
					adapterLocale={currentLocale}
					dateAdapter={AdapterDateFns}
					localeText={enUSPicker.components.MuiLocalizationProvider.defaultProps.localeText}
				>
					<TimePicker
						sx={{
							maxWidth: "150px",
							height: 48,
							marginBottom: 1,
							"& .MuiSvgIcon-root": {
								fontSize: "18px",
							},
							"& .MuiPickersInputBase-root": {
								maxWidth: "150px",
								backgroundColor: "white",
								minHeight: 48,
								marginBottom: 0,
								fontSize: "14px",
							},
							"& .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersInputBase-root:hover": {
								borderColor: "#E5E7EB !important",
							},
							"& .MuiFilledInput-root": {
								backgroundColor: "white",
								minHeight: 48,
								marginBottom: 0,
							},
							"& .MuiFilledInput-input": {
								padding: 1,
							},
						}}
						value={
							schedule?.[day]?.toTime
								? new Date(
										new Date().setHours(
											Number(schedule?.[day]?.toTime.split(":")[0]),
											Number(schedule?.[day]?.toTime.split(":")[1])
										)
									)
								: null
						}
						onChange={(value: Date | null) => {
							if (value && !isValid(value)) {
								return;
							}

							const formattedTime = value ? format(value, "HH:mm") : null;

							setSchedule({
								...schedule,
								[day]: { ...schedule?.[day], toTime: formattedTime as string },
							});
						}}
					/>
				</LocalizationProvider>
			</div>
		</div>
	);
};
